import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Landmark, CheckCircle2, Clock, ExternalLink } from "lucide-react";

export default function ConnectCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try { const { data } = await api.get("/connect/status"); setSt(data); window.dispatchEvent(new CustomEvent("connect-changed", { detail: !!data.ready })); } catch {}
  };
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => { document.removeEventListener("visibilitychange", onFocus); window.removeEventListener("focus", onFocus); };
  }, []);

  useEffect(() => {
    load().then(() => {
      const c = params.get("connect");
      if (c === "done") toast.success("Collegamento Stripe completato. Verifica in corso…");
      if (c === "refresh") toast.info("Sessione scaduta, riprova il collegamento");
      if (c) { params.delete("connect"); setParams(params, { replace: true }); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      localStorage.setItem("enb_connect_started", String(Date.now()));
      window.location.href = data.url;
    } catch (e) {
      const msg = e.response?.data?.detail ? apiError(e.response.data.detail) : `Errore di collegamento (${e.response?.status || "rete"}). Riprova tra poco.`;
      setErr(msg); toast.error(msg); setBusy(false);
    }
  };

  const ready = st?.ready;
  const connected = st?.connected;
  const started = !ready && connected && !!localStorage.getItem("enb_connect_started");
  useEffect(() => { if (ready) localStorage.removeItem("enb_connect_started"); }, [ready]);
  return (
    <div data-testid="connect-card" className="border border-neutral-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Landmark size={18} />
        <h2 className="font-display font-bold">Conto per ricevere i pagamenti</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-3">
        Collega il tuo conto Stripe (obbligatorio per operare): a ogni vendita ricevi automaticamente il 90% (10% a ENBISSY). Qui arrivano anche le tue commissioni di affiliazione.
      </p>
      <p data-testid="payout-timing-notice" className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3 mb-3">
        Tempi di accredito: la quota viene rilasciata subito sul tuo saldo Stripe; il bonifico sul tuo conto bancario arriva di norma entro 5–7 giorni lavorativi (il primo bonifico può richiedere fino a 7–14 giorni), secondo i tempi di Stripe.
      </p>
      <div data-testid="connect-status" className={`flex items-center gap-2 text-sm mb-3 ${ready ? "text-green-700" : "text-amber-700"}`}>
        {ready ? <CheckCircle2 size={16} /> : <Clock size={16} />}
        {st === null ? "…" : ready ? "Conto collegato e attivo" : connected ? "Collegamento in verifica: completa i dati richiesti da Stripe" : "Conto non ancora collegato"}
      </div>
      {st && (st.pending_amount > 0 || st.released_amount > 0) && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">In attesa di rilascio</p><p data-testid="payout-pending" className="font-display font-extrabold">€{st.pending_amount.toFixed(2)}</p></div>
          <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Rilasciati</p><p data-testid="payout-released" className="font-display font-extrabold">€{st.released_amount.toFixed(2)}</p></div>
        </div>
      )}
      {started && <p data-testid="connect-resume-msg" className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3 mb-3">Hai un collegamento Stripe in corso: se sei uscito a metà, tocca "Completa il collegamento" per riprendere da dove eri (i dati già inseriti su Stripe restano salvati).</p>}
      {err && <p data-testid="connect-error" className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl p-3 mb-3">{err}</p>}
      {!ready && (
        <p data-testid="connect-step-hint" className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-xl p-3 mb-3">
          Passo 1 (obbligatorio): collega il conto Stripe. Passo 2: dopo il collegamento potrai aggiungere la carta qui sotto.
        </p>
      )}
      {!ready && (
        <Button data-testid="connect-stripe-btn" onClick={connect} disabled={busy} className="w-full h-11 rounded-full bg-black text-white hover:bg-neutral-800 disabled:opacity-50">
          {busy ? "…" : connected ? "Completa il collegamento" : "Collega conto Stripe"} <ExternalLink size={14} className="ml-1" />
        </Button>
      )}
    </div>
  );
}
